import AppContext from "@common/contexts/App";
import { FormContext } from "@common/contexts/Form";
import { FormGroupContext, type FormGroupContextProps } from "@common/contexts/FormGroup";
import { SubmitContext, type SubmitContextListener } from "@common/contexts/Submit";
import { type GenericMap, type WithChildren } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { ValidationError } from "@common/lib/api";
import { usePrevious } from "@common/lib/hooks";
import { isDev } from "@common/lib/misc";
import { Common } from "@common/types";
import classNames from "classnames";
import { identity, isEqual } from "lodash";
import React, { useImperativeHandle } from "react";
import { Alert } from "../Alert/component";
import {
  type ConfirmProviderModal,
  UnsavedChangesConfirmModalProvider,
  type WithConfirm
} from "../Modal/Modals/Confirm/component";
import FormButtons from "./Buttons/component";
import FormField, { type SelectBehaviour } from "./Field/component";
import { type ValidateFn } from "./Field/lib";
import FormGroup from "./Group/component";
import { GetFinalValuesError, getIsEqual, useFormContextValue } from "./lib";
import "./style.less";

export { FormButtons, FormField, FormGroup };

export type OnChange<T> = (name: keyof T, value: any, values?: Common.GenericMap<any>) => void;

export type OnLoading<T> = (name: keyof T, isLoading: boolean) => void;

export type Submit = () => void | Promise<any>;

export interface ValidationOptions {
  formControlKey: Common.System.FormControlKey;
  primary?: ValidateFn | null;
  validations: Common.System.FormFieldValidation[];
}

export type Validation = ValidationOptions | ValidateFn;

export type FormRequiredMode = "default" | "star";
export interface FormProps<T = {}> {
  children?: any;
  className?: string;
  errorMessage?: string | null;
  isInline?: boolean;
  onChange?: OnChange<T>;
  requiredMode?: FormRequiredMode;
  style?: React.CSSProperties;
  toast?: string;
  // required for Forms (outside of modals), if used inside WizardTabs
  submitContextId?: string;
}

export interface FormFieldInfo<T> {
  link?: Extract<keyof T, string>;
  name: Extract<keyof T, string>;
  getFinalValue?: () => Promise<any>;
  selectBehaviour?: SelectBehaviour;
  setError: (error: string) => void;
  scrollIntoView: () => void;
  validate: () => Promise<string | null | undefined> | string | null | undefined;
}

export type FormControlledRenderParams<T> = {
  defaultValues: Partial<T>;
  isSubmitting: boolean;
  onChange: (name: keyof T, value: any) => void;
  submit: () => void;
  values: T;
  withConfirm: WithConfirm;
};
export type FormControlledProps<T> = Omit<FormProps, "children" | "onChange"> & {
  isEqual?: (defaultValues: Partial<T>, values: Partial<T>) => boolean;
  confirmModalClassName?: string;
  defaultValues: Partial<T>;
  LeaveModal?: ConfirmProviderModal;
  isUnsavedConfirmationDisabled?: boolean;
  onCancel?: () => void;
  onChange?: (name: keyof T, value: any, values: T) => void | T;
  /** pre - reset to default BEFORE submitting, post - reset to default AFTER successful submition  */
  resetOnSubmit?: "pre" | "post";
  submit: (data: T) => Promise<any> | void;

  children: (params: FormControlledRenderParams<T>) => React.ReactElement;
};

export type FormHandles = {
  validate: () => Promise<boolean> | boolean;
  getFinalValues: () => Promise<any>;
};

export const FormControlled = React.forwardRef(
  <T extends object>(
    {
      children,
      submit,
      defaultValues,
      isUnsavedConfirmationDisabled,
      LeaveModal,
      onChange,
      resetOnSubmit,
      ...props
    }: FormControlledProps<T>,
    ref: React.RefObject<FormHandles | null>
  ) => {
    const i18n = useI18n();

    const [error, setError] = React.useState<string | null | undefined>();
    const formRef = React.useRef<HTMLFormElement>(null);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [values, setValues] = React.useState({ ...defaultValues });
    const refValues = React.useRef(values); // WORKAROUND of: when onChange and onSubmit are both triggered on the button click, onSubmit receives the old values (as if there was no onChange).
    const submitContext = React.useContext(SubmitContext);
    const appContext = React.useContext(AppContext);

    useImperativeHandle(ref, () => ({
      validate: contextValue.validate,
      getFinalValues: getFinalValues
    }));

    React.useEffect(() => setError(props.errorMessage), [props.errorMessage]);

    React.useEffect(() => {
      refValues.current = values;
    }, [values]);

    const previousDefaultValues = usePrevious(defaultValues);
    // TODO: proper solution. Reason: with useEffect if we immediately navigate after submit, the confirmation modal appears
    React.useLayoutEffect(() => {
      if (isEqual(previousDefaultValues, defaultValues)) {
        if (isDev()) {
          console.error("defaultValues ref change w/o values change");
        }
      } else {
        setValues({ ...defaultValues });
      }
    }, [defaultValues, previousDefaultValues]);

    const _onChange = React.useCallback(
      (name: Extract<keyof T, string>, value: any) => {
        setValues((prev) => ({ ...prev, [name]: value }));
        if (onChange) {
          const newValues = onChange(name, value, {
            ...values,
            [name]: value
          } as T);
          if (newValues) setValues((prev) => ({ ...prev, ...newValues }));
        }

        const field = contextValue.fields.find((x) => x.link === name);
        if (field && values[field.name] === values[name]) {
          _onChange(field.name, value);
        }
      },
      [onChange, setValues, values]
    );

    const getFinalValues = async (): Promise<T> => {
      const result: GenericMap<any> = {
        // NOTE: include everything what was in the default values (e.g. ID) and onChange events
        // in some place we use onChange to write to form
        ...refValues.current
      };
      for (const field of contextValue.fields) {
        if (field.getFinalValue) {
          try {
            result[field.name] = await field.getFinalValue();
          } catch (e) {
            field.setError(e.message ?? e);
            throw e;
          }
        } else {
          result[field.name] = refValues.current[field.name];
        }
      }
      return result as T;
    };

    const onSubmit = React.useCallback(async () => {
      setIsSubmitting(true);
      setError(null);

      let data: T;
      try {
        data = await getFinalValues();
      } catch {
        setIsSubmitting(false);
        throw new GetFinalValuesError();
      }

      try {
        const resp = submit(data);
        if (resp instanceof Promise) {
          return await resp;
        }
        return resp;
      } catch (e) {
        console.error(e);
        if (e instanceof ValidationError) {
          Object.keys(e.formFieldErrors).forEach((x) => {
            const f = contextValue.fields.find((y) => y.name === x);
            f && (f.setError(e.formFieldErrors[x]), f.scrollIntoView());
          });
        } else {
          setError(e.message ?? e);
          throw e;
        }
      } finally {
        setIsSubmitting(false);
      }
    }, [defaultValues, setError, setIsSubmitting, submit]);

    const hasChanges = React.useMemo(
      () => (props.isEqual ? !props.isEqual(defaultValues, values) : !getIsEqual(defaultValues, values)),
      [defaultValues, values]
    );

    const contextValue = useFormContextValue(_onChange, identity, onSubmit, hasChanges, {
      requiredMode: props.requiredMode ?? "default",
      toast: props.toast ?? i18n.saved
    });

    const onBeforeSubmit = React.useCallback(() => {
      if (resetOnSubmit !== "pre") return;

      setValues({ ...defaultValues });
    }, [defaultValues, resetOnSubmit]);

    const onAfterSubmit = React.useCallback(() => {
      if (resetOnSubmit !== "post") return;

      setValues({ ...defaultValues });
    }, [defaultValues, resetOnSubmit]);

    const validateAndSubmit = React.useCallback(async () => {
      setIsSubmitting(true);

      try {
        if (contextValue) {
          const isValid = await contextValue.validate();
          if (!isValid) return;
        }

        onBeforeSubmit();
        const r = contextValue.submit();
        if (r instanceof Promise) {
          const result = await r;
          onAfterSubmit();
          return result;
        }

        onAfterSubmit();
        return r;
      } finally {
        setIsSubmitting(false);
      }
    }, [contextValue.validate, contextValue.submit, onAfterSubmit, onBeforeSubmit]);

    React.useEffect(() => {
      if (!props.submitContextId || !submitContext) return;

      const listener: SubmitContextListener = {
        callback: contextValue.validate,
        ref: formRef,
        id: props.submitContextId
      };
      submitContext.subscribe(listener);

      return () => {
        submitContext.unsubscribe(listener);
      };
    }, [contextValue.validate, props.submitContextId]);

    // reset group context, so nested components don't accidently use wrong context
    const formGroupContextDummyValue = React.useMemo(
      (): FormGroupContextProps => ({
        fields: [],
        patch: identity,
        register: identity,
        unregister: identity
      }),
      []
    );

    return (
      <UnsavedChangesConfirmModalProvider
        hasChanges={isUnsavedConfirmationDisabled ? false : hasChanges}
        modalClassName={props.confirmModalClassName}
        ConfirmModal={LeaveModal}
      >
        {({ withConfirm }) => (
          <FormContext.Provider value={contextValue}>
            <FormGroupContext.Provider value={formGroupContextDummyValue}>
              {/* form tag limits the scope of browser autocomplete behaviour. E.g. if we have a modal with password only, autofilling it will try autofill any input outside form */}
              <form
                ref={formRef}
                className={classNames("form", props.className, {
                  empty: !contextValue.fields.length,
                  inline: props.isInline,
                  cartly_form: appContext.context === "admin",
                  wsc_form: appContext.context === "next"
                })}
                style={props.style}
                onSubmit={(e) => (e.stopPropagation(), e.preventDefault(), validateAndSubmit())}
              >
                <FormContent>
                  {children({
                    defaultValues,
                    onChange: contextValue.onChange,
                    isSubmitting,
                    submit: validateAndSubmit,
                    values: values as T,
                    withConfirm
                  })}
                </FormContent>

                {error && <Alert color={Common.SystemColor.Red}>{error}</Alert>}
              </form>
            </FormGroupContext.Provider>
          </FormContext.Provider>
        )}
      </UnsavedChangesConfirmModalProvider>
    );
  }
);

export type FormContentProps = WithChildren & {
  style?: React.CSSProperties;
};
export const FormContent = React.forwardRef((props: FormContentProps, ref: React.RefObject<HTMLDivElement>) => (
  <div ref={ref} className="form_content content" style={props.style}>
    {props.children}
  </div>
));
